import type { Dispatcher } from '../dispatcher.js';
import { ToolError } from '../dispatcher.js';
import { ERROR_CODES } from '../wsClient.js';
import { serializeSession, bytesToUuid } from '../serializer.js';
import { getStudioContext, nowIso } from './store.js';
import { EffectFactories } from '@opendaw/studio-core';

const PLUGIN_ID_TO_FACTORY: Readonly<Record<string, object>> = {
  compressor: EffectFactories.Compressor,
  gate: EffectFactories.Gate,
  delay: EffectFactories.Delay,
  dattorroreverb: EffectFactories.DattorroReverb,
  stereotool: EffectFactories.StereoTool,
  maximizer: EffectFactories.Maximizer,
  crusher: EffectFactories.Crusher,
  revamp: EffectFactories.Revamp,
  fold: EffectFactories.Fold,
  exciter: EffectFactories.Exciter,
};

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// Navigate rootBoxAdapter → audioUnits → audioEffects to find an effect by UUID string.
function findEffectAdapter(insert_id: string): Record<string, unknown> | null {
  const ctx = getStudioContext();
  if (ctx === null) return null;
  const project = ctx.getProject();
  if (!isObject(project)) return null;
  const rootBoxAdapter = project['rootBoxAdapter'];
  if (!isObject(rootBoxAdapter)) return null;
  const audioUnits = rootBoxAdapter['audioUnits'];
  if (!isObject(audioUnits) || typeof audioUnits['adapters'] !== 'function') return null;
  const units = (audioUnits['adapters'] as () => unknown)();
  if (!Array.isArray(units)) return null;
  for (const unit of units) {
    if (!isObject(unit)) continue;
    const audioEffects = unit['audioEffects'];
    if (!isObject(audioEffects) || typeof audioEffects['adapters'] !== 'function') continue;
    const effects = (audioEffects['adapters'] as () => unknown)();
    if (!Array.isArray(effects)) continue;
    for (const effect of effects) {
      if (!isObject(effect)) continue;
      const uuidBytes = effect['uuid'];
      if (uuidBytes instanceof Uint8Array && bytesToUuid(uuidBytes) === insert_id) return effect;
    }
  }
  return null;
}

function findAudioUnit(track_id: string): Record<string, unknown> | null {
  const ctx = getStudioContext();
  if (ctx === null) return null;
  const project = ctx.getProject();
  if (!isObject(project)) return null;
  const rootBoxAdapter = project['rootBoxAdapter'];
  if (!isObject(rootBoxAdapter)) return null;
  const audioUnits = rootBoxAdapter['audioUnits'];
  if (!isObject(audioUnits) || typeof audioUnits['adapters'] !== 'function') return null;
  const units = (audioUnits['adapters'] as () => unknown)();
  if (!Array.isArray(units)) return null;
  for (const unit of units) {
    if (!isObject(unit)) continue;
    const uuidBytes = unit['uuid'];
    if (uuidBytes instanceof Uint8Array && bytesToUuid(uuidBytes) === track_id) return unit;
  }
  return null;
}

function readEffectParams(adapter: Record<string, unknown>): Record<string, number> {
  const namedParam = adapter['namedParameter'];
  const params: Record<string, number> = {};
  if (!isObject(namedParam)) return params;
  for (const [key, entry] of Object.entries(namedParam)) {
    if (!isObject(entry) || typeof entry['getValue'] !== 'function') continue;
    const val = (entry['getValue'] as () => unknown)();
    if (typeof val === 'number') params[key] = val;
    else if (typeof val === 'boolean') params[key] = val ? 1 : 0;
  }
  return params;
}

function readEffectBypass(adapter: Record<string, unknown>): boolean {
  const enabledField = adapter['enabledField'];
  if (!isObject(enabledField) || typeof enabledField['getValue'] !== 'function') return false;
  return (enabledField['getValue'] as () => unknown)() === false;
}

function readEffectPluginId(adapter: Record<string, unknown>): string {
  const box = adapter['box'];
  if (!isObject(box)) return 'unknown';
  const ctor = box['constructor'];
  if (!isObject(ctor) || typeof ctor['name'] !== 'string') return 'unknown';
  return (ctor['name'] as string).replace(/DeviceBox$/, '').toLowerCase();
}

function insertNotFound(insert_id: string): ToolError {
  return new ToolError(ERROR_CODES.INVALID_PARAMS, `Insert ${insert_id} does not exist`, {
    code: 'INSERT_NOT_FOUND', insert_id,
  });
}

// Get the project's editing.modify bound fn for mutations.
function getEditingModify(): ((fn: () => void) => void) | null {
  const ctx = getStudioContext();
  if (ctx === null) return null;
  const project = ctx.getProject();
  if (!isObject(project)) return null;
  const editing = project['editing'];
  if (!isObject(editing) || typeof editing['modify'] !== 'function') return null;
  return (fn: () => void) => (editing['modify'] as (fn: () => void) => void)(fn);
}

// --- listInserts ------------------------------------------------------------

interface ListInsertEntry {
  insert_id: string;
  track_id: string;
  plugin_id: string;
  position: number;
  bypass: boolean;
  preset_name?: string;
}

interface ListInsertsParams {
  track_id?: string;
}

function listInserts(params: ListInsertsParams): { inserts: ListInsertEntry[] } {
  const ctx = getStudioContext();
  if (ctx === null) return { inserts: [] };
  const project = serializeSession(ctx.getProject());
  const inserts: ListInsertEntry[] = [];
  for (const track of project.tracks) {
    if (params?.track_id !== undefined && track.id !== params.track_id) continue;
    for (let pos = 0; pos < track.inserts.length; pos++) {
      const ins = track.inserts[pos];
      const entry: ListInsertEntry = {
        insert_id: ins.id,
        track_id: track.id,
        plugin_id: ins.plugin_id,
        position: pos,
        bypass: ins.bypass,
      };
      if (ins.preset_name !== undefined) entry.preset_name = ins.preset_name;
      inserts.push(entry);
    }
  }
  if (params?.track_id !== undefined && inserts.length === 0) {
    const exists = project.tracks.some((t) => t.id === params.track_id);
    if (!exists) {
      throw new ToolError(ERROR_CODES.INVALID_PARAMS, `Track ${params.track_id} does not exist`, {
        code: 'TRACK_NOT_FOUND', track_id: params.track_id,
      });
    }
  }
  return { inserts };
}

// --- getInsertParameters ----------------------------------------------------

interface GetInsertParametersParams {
  insert_id: string;
}

interface GetInsertParametersResult {
  insert_id: string;
  plugin_id: string;
  bypass: boolean;
  params: Record<string, number>;
}

function getInsertParameters(params: GetInsertParametersParams): GetInsertParametersResult {
  if (!params || typeof params.insert_id !== 'string') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'getInsertParameters: insert_id required');
  }
  const adapter = findEffectAdapter(params.insert_id);
  if (adapter === null) throw insertNotFound(params.insert_id);
  return {
    insert_id: params.insert_id,
    plugin_id: readEffectPluginId(adapter),
    bypass: readEffectBypass(adapter),
    params: readEffectParams(adapter),
  };
}

// --- setInsertParameters ----------------------------------------------------

interface SetInsertParametersParams {
  insert_id: string;
  params: Record<string, number>;
}

interface SetInsertParametersResult {
  insert_id: string;
  params: Record<string, number>;
  applied_at: string;
}

function setInsertParameters(params: SetInsertParametersParams): SetInsertParametersResult {
  if (!params || typeof params.insert_id !== 'string' || !isObject(params.params)) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setInsertParameters: insert_id and params required');
  }
  const adapter = findEffectAdapter(params.insert_id);
  if (adapter === null) throw insertNotFound(params.insert_id);
  const modify = getEditingModify();
  if (modify === null) throw insertNotFound(params.insert_id);
  const namedParam = adapter['namedParameter'];
  modify(() => {
    if (!isObject(namedParam)) return;
    for (const [key, value] of Object.entries(params.params)) {
      const dotIdx = key.indexOf('.');
      const entry = dotIdx === -1
        ? namedParam[key]
        : (() => {
            const group = namedParam[key.slice(0, dotIdx)];
            return isObject(group) ? group[key.slice(dotIdx + 1)] : undefined;
          })();
      if (!isObject(entry) || typeof entry['setValue'] !== 'function') continue;
      if (!Number.isFinite(value)) continue;
      const current = typeof entry['getValue'] === 'function'
        ? (entry['getValue'] as () => unknown)()
        : undefined;
      if (typeof current === 'boolean') {
        (entry['setValue'] as (v: boolean) => void)(value >= 0.5);
      } else {
        (entry['setValue'] as (v: number) => void)(value);
      }
    }
  });
  return { insert_id: params.insert_id, params: readEffectParams(adapter), applied_at: nowIso() };
}

// --- setInsertBypass --------------------------------------------------------

interface SetInsertBypassParams {
  insert_id: string;
  bypass: boolean;
}

interface SetInsertBypassResult {
  insert_id: string;
  bypass: boolean;
  applied_at: string;
}

function setInsertBypass(params: SetInsertBypassParams): SetInsertBypassResult {
  if (!params || typeof params.insert_id !== 'string' || typeof params.bypass !== 'boolean') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setInsertBypass: insert_id and bypass required');
  }
  const adapter = findEffectAdapter(params.insert_id);
  if (adapter === null) throw insertNotFound(params.insert_id);
  const modify = getEditingModify();
  if (modify === null) throw insertNotFound(params.insert_id);
  const enabledField = adapter['enabledField'];
  if (isObject(enabledField) && typeof enabledField['setValue'] === 'function') {
    // enabled = !bypass in openDAW (enabled:true means the effect is active).
    modify(() => (enabledField['setValue'] as (v: boolean) => void)(!params.bypass));
  }
  return { insert_id: params.insert_id, bypass: params.bypass, applied_at: nowIso() };
}

// --- createInsert ------------------------------------------------------------

interface CreateInsertParams {
  track_id: string;
  plugin_id: string;
  position?: number;
  params?: Record<string, number>;
}

interface CreateInsertResult {
  insert_id: string;
  track_id: string;
  plugin_id: string;
  position: number;
  applied_at: string;
}

function createInsert(params: CreateInsertParams): CreateInsertResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.plugin_id !== 'string') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'createInsert: track_id and plugin_id required');
  }
  const factory = PLUGIN_ID_TO_FACTORY[params.plugin_id];
  if (factory === undefined) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, `Plugin ${params.plugin_id} not in catalog`, {
      code: 'PLUGIN_NOT_FOUND', plugin_id: params.plugin_id,
    });
  }
  const audioUnit = findAudioUnit(params.track_id);
  if (audioUnit === null) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, `Track ${params.track_id} does not exist`, {
      code: 'TRACK_NOT_FOUND', track_id: params.track_id,
    });
  }
  const audioEffects = audioUnit['audioEffects'];
  if (!isObject(audioEffects) || typeof audioEffects['field'] !== 'function') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, `Cannot access effects chain for track ${params.track_id}`);
  }
  const hostField = (audioEffects['field'] as () => unknown)();
  const ctx = getStudioContext();
  if (ctx === null) throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'No project open');
  const project = ctx.getProject();
  if (!isObject(project)) throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'No project open');
  const api = project['api'];
  if (!isObject(api) || typeof api['insertEffect'] !== 'function') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'Project API unavailable');
  }
  const currentEffects = (audioEffects['adapters'] as () => unknown)();
  const currentCount = Array.isArray(currentEffects) ? currentEffects.length : 0;
  const modify = getEditingModify();
  if (modify === null) throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'Editing API unavailable');
  let insertId = '';
  modify(() => {
    const box = (api['insertEffect'] as (f: unknown, factory: unknown, idx: number) => unknown)(
      hostField, factory, params.position ?? Number.MAX_SAFE_INTEGER
    );
    if (isObject(box)) {
      const address = box['address'];
      if (isObject(address)) {
        const uuid = address['uuid'];
        if (uuid instanceof Uint8Array) insertId = bytesToUuid(uuid);
      }
    }
  });
  if (insertId === '') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'createInsert: failed to obtain insert UUID from created box');
  }
  if (params.params && Object.keys(params.params).length > 0) {
    const newAdapter = findEffectAdapter(insertId);
    if (newAdapter !== null) {
      const namedParam = newAdapter['namedParameter'];
      modify(() => {
        if (!isObject(namedParam)) return;
        for (const [key, value] of Object.entries(params.params!)) {
          const dotIdx = key.indexOf('.');
          const entry = dotIdx === -1
            ? namedParam[key]
            : (() => {
                const group = namedParam[key.slice(0, dotIdx)];
                return isObject(group) ? group[key.slice(dotIdx + 1)] : undefined;
              })();
          if (!isObject(entry) || typeof entry['setValue'] !== 'function') continue;
          if (!Number.isFinite(value)) continue;
          const current = typeof entry['getValue'] === 'function'
            ? (entry['getValue'] as () => unknown)() : undefined;
          if (typeof current === 'boolean') {
            (entry['setValue'] as (v: boolean) => void)(value >= 0.5);
          } else {
            (entry['setValue'] as (v: number) => void)(value);
          }
        }
      });
    }
  }
  const position = params.position !== undefined ? Math.min(params.position, currentCount) : currentCount;
  return { insert_id: insertId, track_id: params.track_id, plugin_id: params.plugin_id, position, applied_at: nowIso() };
}

// --- deleteInsert ------------------------------------------------------------

interface DeleteInsertParams {
  insert_id: string;
}

interface DeleteInsertResult {
  insert_id: string;
  deleted: boolean;
  applied_at: string;
}

function deleteInsert(params: DeleteInsertParams): DeleteInsertResult {
  if (!params || typeof params.insert_id !== 'string') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'deleteInsert: insert_id required');
  }
  const adapter = findEffectAdapter(params.insert_id);
  if (adapter === null) {
    return { insert_id: params.insert_id, deleted: false, applied_at: nowIso() };
  }
  const box = adapter['box'];
  if (!isObject(box) || typeof box['delete'] !== 'function') {
    return { insert_id: params.insert_id, deleted: false, applied_at: nowIso() };
  }
  const modify = getEditingModify();
  if (modify === null) return { insert_id: params.insert_id, deleted: false, applied_at: nowIso() };
  modify(() => (box['delete'] as () => void)());
  return { insert_id: params.insert_id, deleted: true, applied_at: nowIso() };
}

export const insertHandlers = {
  listInserts,
  getInsertParameters,
  setInsertParameters,
  setInsertBypass,
  createInsert,
  deleteInsert,
} as const;

export function registerInsertHandlers(d: Dispatcher): void {
  d.register<ListInsertsParams, { inserts: ListInsertEntry[] }>('listInserts', listInserts);
  d.register<GetInsertParametersParams, GetInsertParametersResult>('getInsertParameters', getInsertParameters);
  d.register<SetInsertParametersParams, SetInsertParametersResult>('setInsertParameters', setInsertParameters);
  d.register<SetInsertBypassParams, SetInsertBypassResult>('setInsertBypass', setInsertBypass);
  d.register<CreateInsertParams, CreateInsertResult>('createInsert', createInsert);
  d.register<DeleteInsertParams, DeleteInsertResult>('deleteInsert', deleteInsert);
}
